import React, { useState, useEffect } from 'react';
import { useCompany } from '../../component/CompanySelection/CompanyContext';
import Tile from '../Tile/Tile';
import * as service from '../../service/ProgramOverviewTileGroupService';
import * as DateUtil from '../../util/DateUtility';
import { formatAmount, formatDollarAmount, getUrl } from '../../util/EfupUtility';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import './ProgramOverviewTileGroup.css'
import refresh from '../../images/refresh-icon.svg';
import LastRefreshed from '../Refresh/LastRefreshed';

export const WEEK_CACHE_KEY = 'weekPayments';
export const MONTH_CACHE_KEY = 'monthPayments';
export const QUARTER_CACHE_KEY = 'quarterPayments';
export const TOTAL_CACHE_KEY = 'thirtyDayTotal';
export const LAST_REFRESHED_TIME = 'lastRefreshedTime';

export default function ProgramOverviewTileGroup(props) {
    const { companyNumber } = useCompany();

    const [thirtyDayRange] = useState(DateUtil.getPastXDays(30));
    const [currentWeekRange] = useState(DateUtil.getCurrentWeekDateRange());
    const [currentMonthRange] = useState(DateUtil.getCurrentMonthDateRange());
    const [currentQuarterRange] = useState(DateUtil.getCurrentQuarterDateRange());

    const [transactionsTotal, setTransactionsTotal] = useState(window.sessionStorage.getItem(TOTAL_CACHE_KEY));
    const [paymentsThisWeek, setPaymentsThisWeek] = useState(window.sessionStorage.getItem(WEEK_CACHE_KEY));
    const [paymentsThisMonth, setPaymentsThisMonth] = useState(window.sessionStorage.getItem(MONTH_CACHE_KEY));
    const [paymentsThisQuarter, setPaymentsThisQuarter] = useState(window.sessionStorage.getItem(QUARTER_CACHE_KEY));
    const [lastRefreshedTimestamp, setLastRefreshedTimestamp] = useState(window.sessionStorage.getItem(LAST_REFRESHED_TIME));

    const [isLoading30DayTotal, setIsLoading30DayTotal] = useState(false);
    const [isLoadingWeek, setIsLoadingWeek] = useState(false);
    const [isLoadingMonth, setIsLoadingMonth] = useState(false);
    const [isLoadingQuarter, setIsLoadingQuarter] = useState(false);

    const getCompanyTotalsForRange = function (company, range, callbackHandler) {
        let startOn = (range.startOn) ? DateUtil.toIsoDateString(range.startOn) : ``;
        let endOn = (range.endOn) ? DateUtil.toIsoDateString(range.endOn) : ``;

        let request = service.RequestParameters(company, startOn, endOn);
        service.makeRequest(request, callbackHandler);
    };

    // Past 30 days
    useEffect(() => {
        if (!companyNumber || typeof companyNumber === 'undefined') {
            return;
        }

        setTransactionsTotal(window.sessionStorage.getItem(TOTAL_CACHE_KEY));
        if (transactionsTotal == null) {
            setIsLoading30DayTotal(true);
            getCompanyTotalsForRange(companyNumber, thirtyDayRange, (result) => {
                window.sessionStorage.setItem(TOTAL_CACHE_KEY, result?.totalSpendAmount);
                setTransactionsTotal(window.sessionStorage.getItem(TOTAL_CACHE_KEY));
                setIsLoading30DayTotal(false);
            });
        }
    }, [companyNumber, transactionsTotal, thirtyDayRange]);

    // Week
    useEffect(() => {
        if (!companyNumber || typeof companyNumber === 'undefined') {
            return;
        }

        setPaymentsThisWeek(window.sessionStorage.getItem(WEEK_CACHE_KEY));
        if (paymentsThisWeek == null) {
            setIsLoadingWeek(true);
            getCompanyTotalsForRange(companyNumber, currentWeekRange, (result) => {
                window.sessionStorage.setItem(WEEK_CACHE_KEY, result?.totalNonClosedPayments);
                setPaymentsThisWeek(window.sessionStorage.getItem(WEEK_CACHE_KEY));
                setIsLoadingWeek(false);
            });
        }
    }, [companyNumber, paymentsThisWeek, currentWeekRange]);

    // Month
    useEffect(() => {
        if (!companyNumber || typeof companyNumber === 'undefined') {
            return;
        }

        setPaymentsThisMonth(window.sessionStorage.getItem(MONTH_CACHE_KEY));
        if (paymentsThisMonth == null) {
            setIsLoadingMonth(true);
            getCompanyTotalsForRange(companyNumber, currentMonthRange, (result) => {
                window.sessionStorage.setItem(MONTH_CACHE_KEY, result?.totalNonClosedPayments);
                setPaymentsThisMonth(window.sessionStorage.getItem(MONTH_CACHE_KEY));
                setIsLoadingMonth(false);
            });
        }
    }, [companyNumber, paymentsThisMonth, currentMonthRange]);

    // Quarter
    useEffect(() => {
        if (!companyNumber || typeof companyNumber === 'undefined') {
            return;
        }

        setPaymentsThisQuarter(window.sessionStorage.getItem(QUARTER_CACHE_KEY));
        if (paymentsThisQuarter == null) {
            setIsLoadingQuarter(true);
            getCompanyTotalsForRange(companyNumber, currentQuarterRange, (result) => {
                window.sessionStorage.setItem(QUARTER_CACHE_KEY, result?.totalNonClosedPayments);
                setPaymentsThisQuarter(window.sessionStorage.getItem(QUARTER_CACHE_KEY));
                setIsLoadingQuarter(false);
            });
        }
    }, [companyNumber, paymentsThisQuarter, currentQuarterRange]);

    useEffect(() => {
        setLastRefreshedTimestamp(window.sessionStorage.getItem(LAST_REFRESHED_TIME));
        if (lastRefreshedTimestamp == null) {
            window.sessionStorage.setItem(LAST_REFRESHED_TIME, Date());
            setLastRefreshedTimestamp(window.sessionStorage.getItem(LAST_REFRESHED_TIME))
        }
    })

    const paymentsLinkFromDateRange = function (range) {
        let startDate = range.startOn;
        let endDate = range.endOn;

        let link = { path: getUrl(companyNumber, '/payments'), state: { search: { startDate, endDate } } };
        return link;
    };

    const loadingOrValue = function (isLoading, value, formattedValue, tileId) {
        if (isLoading) {
            return <LoadingSpinner blue={tileId !== 'transactions-total'} testId={`${tileId}-loading-spinner`} />;
        }

        if (value === null || typeof value === 'undefined') {
            return '';
        }

        return formattedValue;
    }

    const refreshData = function () {
        window.location.reload()
    }

    return (
        <>
            <div className="row">
                <div className="col overview">
                    <table className="overViewTable">
                        <tbody>
                            <tr>
                                <td>
                                    <div className="sectionTitle overviewTitle" data-testid="program-overview-tile-group-label">Program Overview</div>
                                </td>

                                <td>
                                    <div className="overviewRefresh">
                                        <div className="refreshImgAndText" onClick={refreshData}>
                                            <img src={refresh} />
                                            <span className="refreshText">Refresh</span>
                                        </div>
                                        <div className="efup-refresh-tooltip cb-font-poppins"><span className="tooltipChar">i</span>
                                            <span data-testid="efup-refresh-tooltip" className="refreshTipText">
                                                Click Refresh to update the page and view the most current data. Payment data is not automatically refreshed.
                                            </span>
                                        </div>
                                    </div>
                                </td>
                            </tr>

                            <tr>
                                <td></td>
                                <td className="lastUpdatedTable">
                                    <div className="test2">
                                        <div className="test2container">
                                            <span className="lastUpdatedText">Last Updated: <LastRefreshed dateAsString={lastRefreshedTimestamp} /></span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>


                </div>
            </div>

            <div className="row dashCards">
                <div className="col-4 overview-mobile">
                    <Tile className="dashCardBlueBig" title="Transactions Total" footer="for the past 30 days">{loadingOrValue(isLoading30DayTotal, transactionsTotal, formatDollarAmount(transactionsTotal), 'transactions-total')}</Tile>
                </div>
                <div className="col overview-mobile">
                    <Tile title="Payments this week" link={paymentsLinkFromDateRange(currentWeekRange)} isLoading={isLoadingWeek}>{loadingOrValue(isLoadingWeek, paymentsThisWeek, formatAmount(paymentsThisWeek, 0), 'payments-this-week')}</Tile>
                </div>
                <div className="col overview-mobile">
                    <Tile title="Payments this month" link={paymentsLinkFromDateRange(currentMonthRange)} isLoading={isLoadingMonth}>{loadingOrValue(isLoadingMonth, paymentsThisMonth, formatAmount(paymentsThisMonth, 0), 'payments-this-month')}</Tile>
                </div>
                <div className="col overview-mobile">
                    <Tile title="Payments this quarter" link={paymentsLinkFromDateRange(currentQuarterRange)} isLoading={isLoadingQuarter}>{loadingOrValue(isLoadingQuarter, paymentsThisQuarter, formatAmount(paymentsThisQuarter, 0), 'payments-this-quarter')}</Tile>
                </div>
            </div>
        </>
    );
};